/**
 * Backup Codes Command
 *
 * Securely store and manage 2FA recovery/backup codes.
 */

import inquirer from 'inquirer';
import chalk from 'chalk';
import { CredentialService } from '../core/service';
import * as output from '../utils/output';
import { confirm } from '../utils/prompts';

/**
 * Generate credential name for backup codes
 */
function getCredentialName(service: string, account?: string): string {
  const normalized = service.toUpperCase().replace(/[^A-Z0-9]/g, '_');
  if (account) {
    const accountNorm = account.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
    return `BACKUP_CODES_${normalized}_${accountNorm}`;
  }
  return `BACKUP_CODES_${normalized}`;
}

/**
 * Parse stored backup codes
 */
function parseCodes(value: string): { codes: string[]; usedIndices: number[] } {
  try {
    const data = JSON.parse(value);
    return {
      codes: data.codes || [],
      usedIndices: data.usedIndices || [],
    };
  } catch {
    // Legacy format: just newline-separated codes
    return {
      codes: value.split('\n').filter(Boolean),
      usedIndices: [],
    };
  }
}

/**
 * Serialize codes for storage
 */
function serializeCodes(codes: string[], usedIndices: number[]): string {
  return JSON.stringify({ codes, usedIndices });
}

interface AddOptions {
  account?: string;
  quiet?: boolean;
}

export async function backupCodesAddCommand(service: string, options: AddOptions): Promise<void> {
  const svc = new CredentialService();

  const vaults = await svc.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  await svc.init();

  const credName = getCredentialName(service, options.account);

  // Check if already exists
  const existing = await svc.get(credName);
  if (existing) {
    const overwrite = await confirm(`Backup codes for ${service} already exist. Overwrite?`);
    if (!overwrite) {
      output.info('Cancelled.');
      return;
    }
  }

  console.log('');
  output.header(`Add Backup Codes: ${service}`);
  if (options.account) {
    output.keyValue('Account', options.account);
  }
  output.divider();
  console.log(chalk.gray('  Enter your backup codes, one per line.'));
  console.log(chalk.gray('  Press Enter twice when done.\n'));

  // Collect codes interactively
  const codes: string[] = [];
  let emptyCount = 0;

  const readline = await import('readline');
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  const askForCode = (): Promise<string> => {
    return new Promise((resolve) => {
      const num = codes.length + 1;
      rl.question(chalk.cyan(`  Code ${num}: `), (answer) => {
        resolve(answer.trim());
      });
    });
  };

  while (emptyCount < 1) {
    const code = await askForCode();
    if (code === '') {
      emptyCount++;
    } else {
      emptyCount = 0;
      codes.push(code);
    }
  }

  rl.close();

  if (codes.length === 0) {
    output.error('No codes entered.');
    process.exit(1);
  }

  // Store encrypted
  const value = serializeCodes(codes, []);
  const metadata = {
    description: `Backup codes for ${service}${options.account ? ` (${options.account})` : ''}`,
    tags: ['backup-codes', service.toLowerCase()],
  };

  const spin = output.spinner('Encrypting backup codes...');
  spin.start();

  await svc.add(credName, value, metadata);

  spin.stop();

  console.log('');
  output.success(`Saved ${codes.length} backup codes for ${service}`);
  output.info(`Retrieve with: secretsage backup-codes show ${service}`);
  console.log('');
}

interface ListOptions {
  json?: boolean;
}

export async function backupCodesListCommand(options: ListOptions): Promise<void> {
  const svc = new CredentialService();

  const vaults = await svc.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  await svc.init();

  const allCreds = await svc.list();
  const backupCreds = allCreds.filter((c) => c.name.startsWith('BACKUP_CODES_'));

  if (options.json) {
    const services = backupCreds.map((c) => {
      const parts = c.name.replace('BACKUP_CODES_', '').split('_');
      // Try to detect if last part is account
      return {
        name: c.name,
        service: parts[0].toLowerCase(),
        account: parts.length > 1 ? parts.slice(1).join('_') : undefined,
        addedAt: c.createdAt,
      };
    });
    output.jsonOutput({ services, count: services.length });
    return;
  }

  if (backupCreds.length === 0) {
    output.info('No backup codes stored.');
    output.info('Add with: secretsage backup-codes add <service>');
    return;
  }

  console.log('');
  output.header('Stored Backup Codes');
  output.divider();

  for (const cred of backupCreds) {
    const servicePart = cred.name.replace('BACKUP_CODES_', '');
    const parts = servicePart.split('_');
    const service = parts[0].toLowerCase();
    const account = parts.length > 1 ? parts.slice(1).join('_').toLowerCase() : null;

    // Get the codes to show count
    const full = await svc.get(cred.name);
    if (full) {
      const { codes, usedIndices } = parseCodes(full.value);
      const remaining = codes.length - usedIndices.length;
      const status = remaining === codes.length
        ? chalk.green(`${codes.length} codes`)
        : chalk.yellow(`${remaining}/${codes.length} remaining`);

      console.log(`  ${chalk.cyan(service)}${account ? chalk.gray(` (${account})`) : ''}`);
      console.log(`    ${status}`);
    }
  }

  console.log('');
}

interface ShowOptions {
  account?: string;
  yes?: boolean;
}

export async function backupCodesShowCommand(service: string, options: ShowOptions): Promise<void> {
  const svc = new CredentialService();

  const vaults = await svc.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  await svc.init();

  const credName = getCredentialName(service, options.account);
  const cred = await svc.get(credName);

  if (!cred) {
    output.error(`No backup codes found for ${service}`);
    const allCreds = await svc.list();
    const similar = allCreds.filter((c) =>
      c.name.startsWith('BACKUP_CODES_') &&
      c.name.toLowerCase().includes(service.toLowerCase())
    );
    if (similar.length > 0) {
      output.info('Did you mean:');
      similar.forEach((c) => console.log(`  ${c.name.replace('BACKUP_CODES_', '').toLowerCase()}`));
    }
    process.exit(1);
  }

  // Confirm before showing
  if (!options.yes) {
    console.log('');
    output.warning('You are about to display sensitive backup codes.');
    const confirmed = await confirm('Show backup codes on screen?');
    if (!confirmed) {
      output.info('Cancelled.');
      return;
    }
  }

  const { codes, usedIndices } = parseCodes(cred.value);

  console.log('');
  output.header(`Backup Codes: ${service}`);
  if (options.account) {
    output.keyValue('Account', options.account);
  }
  output.divider();

  codes.forEach((code, i) => {
    const num = String(i + 1).padStart(2, ' ');
    if (usedIndices.includes(i)) {
      console.log(chalk.gray(`  ${num}. ${chalk.strikethrough(code)} (used)`));
    } else {
      console.log(`  ${chalk.green(num)}. ${chalk.white(code)}`);
    }
  });

  const remaining = codes.length - usedIndices.length;
  console.log('');
  output.info(`${remaining} of ${codes.length} codes remaining`);
  console.log('');
}

interface UseOptions {
  account?: string;
  index?: string;
}

export async function backupCodesUseCommand(service: string, options: UseOptions): Promise<void> {
  const svc = new CredentialService();

  const vaults = await svc.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  await svc.init();

  const credName = getCredentialName(service, options.account);
  const cred = await svc.get(credName);

  if (!cred) {
    output.error(`No backup codes found for ${service}`);
    process.exit(1);
  }

  const { codes, usedIndices } = parseCodes(cred.value);
  const availableIndices = codes
    .map((_, i) => i)
    .filter((i) => !usedIndices.includes(i));

  if (availableIndices.length === 0) {
    output.error('All backup codes have been used!');
    output.warning('You should generate new backup codes for this service.');
    process.exit(1);
  }

  let indexToMark: number;

  if (options.index) {
    indexToMark = parseInt(options.index, 10) - 1; // Convert to 0-based
    if (isNaN(indexToMark) || indexToMark < 0 || indexToMark >= codes.length) {
      output.error(`Invalid index. Must be 1-${codes.length}`);
      process.exit(1);
    }
    if (usedIndices.includes(indexToMark)) {
      output.error(`Code #${indexToMark + 1} is already marked as used.`);
      process.exit(1);
    }
  } else {
    // Prompt to select which code was used
    const choices = availableIndices.map((i) => ({
      name: `${i + 1}. ${codes[i].substring(0, 4)}${'*'.repeat(codes[i].length - 4)}`,
      value: i,
    }));

    const { selected } = await inquirer.prompt([
      {
        type: 'list',
        name: 'selected',
        message: 'Which code did you use?',
        choices,
      },
    ]);

    indexToMark = selected;
  }

  // Update used indices
  const newUsedIndices = [...usedIndices, indexToMark];
  const newValue = serializeCodes(codes, newUsedIndices);

  await svc.add(credName, newValue, cred.metadata);

  const remaining = codes.length - newUsedIndices.length;
  output.success(`Marked code #${indexToMark + 1} as used.`);
  output.info(`${remaining} codes remaining for ${service}`);

  if (remaining <= 2) {
    console.log('');
    output.warning('Running low on backup codes! Consider generating new ones.');
  }
}
